import { ProviderSubscriptionStatus } from "./market-data-contracts.js";

export function createMockAdapterHarness() {
  const instances = new Set();
  const subscriptionCounts = new Map();
  const outcomes = new Map();
  let connectionCount = 0;
  let closedCount = 0;

  class MockProviderAdapter {
    constructor(handlers) {
      this.handlers = handlers;
      this.active = new Map();
      this.closed = false;
      instances.add(this);
    }

    async connect() {
      connectionCount += 1;
      return this;
    }

    async subscribe(entries) {
      return entries.map(entry => {
        const request = entry.request || entry;
        const key = entry.key;
        const status = outcomes.get(request.providerSymbol)
          || (request.providerSymbol.includes("UNENTITLED")
            ? ProviderSubscriptionStatus.UNENTITLED
            : ProviderSubscriptionStatus.SUBSCRIBED);
        if (status === ProviderSubscriptionStatus.SUBSCRIBED) {
          this.active.set(key, request);
          subscriptionCounts.set(key, (subscriptionCounts.get(key) || 0) + 1);
        }
        const delayed = String(request.expectedAvailability || "").toLowerCase() === "delayed";
        return {
          key,
          status,
          metadata: { entitlement: status === "subscribed" ? delayed ? "DL" : "RT" : null }
        };
      });
    }

    async unsubscribe(entries) {
      for (const entry of entries) this.active.delete(entry.key);
    }

    getSessionStats() {}

    close() {
      if (this.closed) return;
      this.closed = true;
      closedCount += 1;
      this.active.clear();
      instances.delete(this);
    }
  }

  return Object.freeze({
    factory: handlers => new MockProviderAdapter(handlers),
    emit(raw) {
      const message = Object.freeze({
        provider: "QuoteMedia",
        providerSymbol: String(raw.providerSymbol || raw.symbol || "").toUpperCase(),
        exchange: raw.exchange || null,
        type: String(raw.type || "PRICEDATA").toUpperCase(),
        eventTimestampUtc: raw.eventTimestampUtc || new Date().toISOString(),
        eventTimestampMs: Date.parse(raw.eventTimestampUtc || new Date().toISOString()),
        providerSequence: raw.providerSequence === undefined ? null : String(raw.providerSequence),
        providerEventId: raw.providerEventId || null,
        receiptTimestampUtc: new Date().toISOString(),
        receiptTimestampMs: Date.now(),
        price: numberOrNull(raw.price ?? raw.last),
        size: numberOrNull(raw.size),
        bid: numberOrNull(raw.bid),
        ask: numberOrNull(raw.ask),
        bidSize: numberOrNull(raw.bidSize),
        askSize: numberOrNull(raw.askSize),
        volume: numberOrNull(raw.volume),
        change: numberOrNull(raw.change),
        percentChange: numberOrNull(raw.percentChange),
        session: raw.session || null,
        entitlement: raw.entitlement || "RT",
        providerTiming: Object.freeze({})
      });
      for (const instance of instances) instance.handlers.onMessage?.(message);
    },
    disconnect() {
      for (const instance of [...instances]) {
        instance.handlers.onEvent?.({ kind: "close", detail: { code: 1006 } });
      }
    },
    setOutcome(providerSymbol, status = ProviderSubscriptionStatus.SUBSCRIBED) {
      outcomes.set(String(providerSymbol || "").trim().toUpperCase(), status);
    },
    snapshot() {
      return {
        connectionCount,
        closedCount,
        activeConnections: instances.size,
        subscriptionCounts: Object.fromEntries(subscriptionCounts),
        activeSubscriptions: [...instances]
          .flatMap(instance => [...instance.active.keys()])
          .sort()
      };
    }
  });
}

function numberOrNull(value) {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}
